import type {
  IIndirectTaxAvailableRegistrationV2,
  IIndirectTaxTaxTypeOptionV2,
} from '@commenda/api/indirect-tax/registrations/types';
import {
  Badge,
  Checkbox,
  DatePicker,
  LoadingIndicator,
  SingleCombobox,
} from '@commenda/kakashi';
import { FREQUENCY_DISPLAY_NAMES } from '@commenda/utils';
import { RadioCardKS } from '../../../components';
import { IndirectTaxRegistrationField } from './IndirectTaxRegistrationField';
import {
  IndirectTaxRegistrationHelper,
  type IndirectTaxRegistrationTaxTypeDetailsMap,
} from '../utils/IndirectTaxRegistrationHelper';

interface IndirectTaxRegistrationSelectorProps {
  availableRegistrations: IIndirectTaxAvailableRegistrationV2[];
  isLoading: boolean;
  selectedRegistrationId: string;
  onRegistrationChange: (registrationId: string) => void;
  selectedTaxTypeOption: IIndirectTaxTaxTypeOptionV2 | null;
  selectedRegistrationTaxTypeId: string;
  selectedTaxTypes: string[];
  onTaxTypeOptionChange: (
    registrationTaxTypeId: string,
    taxTypes: string[]
  ) => void;
  onOptionalTaxTypeToggle: (taxType: string, checked: boolean) => void;
  selectedFrequency: string;
  onFrequencyChange: (frequency: string) => void;
  effectiveStartDate: string;
  onEffectiveDateChange: (date: string) => void;
  taxTypeDetailsMap?: IndirectTaxRegistrationTaxTypeDetailsMap;
  isEditMode?: boolean;
  registrationName?: string;
  hideFrequencyAndDate?: boolean;
  isRegistrationLocked?: boolean;
}

export function IndirectTaxRegistrationSelector({
  availableRegistrations,
  isLoading,
  selectedRegistrationId,
  onRegistrationChange,
  selectedTaxTypeOption,
  selectedRegistrationTaxTypeId,
  selectedTaxTypes,
  onTaxTypeOptionChange,
  onOptionalTaxTypeToggle,
  selectedFrequency,
  onFrequencyChange,
  effectiveStartDate,
  onEffectiveDateChange,
  taxTypeDetailsMap,
  isEditMode = false,
  registrationName,
  hideFrequencyAndDate = false,
  isRegistrationLocked = false,
}: IndirectTaxRegistrationSelectorProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-sm py-xl">
        <LoadingIndicator size={16} />
        <span className="txt-small-regular text-text-tertiary">
          Loading available registrations...
        </span>
      </div>
    );
  }

  if (availableRegistrations.length === 0) {
    return (
      <p className="txt-small-regular text-text-tertiary py-xl">
        No registrations available for this region.
      </p>
    );
  }

  const selectedReg = availableRegistrations.find(
    (r) => r.registrationId === selectedRegistrationId
  );
  const taxTypeOptions = selectedReg?.taxTypeOptions ?? [];

  const optionalTaxTypes = selectedTaxTypeOption
    ? IndirectTaxRegistrationHelper.getOptionalTaxTypes(selectedTaxTypeOption)
    : [];

  const formatFreq = (f: string) => FREQUENCY_DISPLAY_NAMES[f] || f;
  const frequencyOptions = IndirectTaxRegistrationHelper.getFrequencyOptions(
    taxTypeOptions,
    formatFreq
  ).map((f) => ({
    value: f.primary,
    label: f.label,
  }));

  return (
    <div className="flex flex-col gap-3xl">
      <IndirectTaxRegistrationField
        availableRegistrations={availableRegistrations}
        isLoading={isLoading}
        selectedRegistrationId={selectedRegistrationId}
        onRegistrationChange={onRegistrationChange}
        isLocked={isEditMode || isRegistrationLocked}
        registrationName={registrationName}
      />

      {selectedRegistrationId && taxTypeOptions.length > 0 && (
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xs">
            <p className="txt-small-medium text-text-secondary">
              Tax type
              {!isEditMode && (
                <span className="text-utility-violet-600 ml-0.5">*</span>
              )}
            </p>
          </div>
          <div className="flex flex-col gap-md">
            {taxTypeOptions.map((opt) => {
              const requiredTaxTypes =
                IndirectTaxRegistrationHelper.getRequiredTaxTypes(opt);
              const isSelected =
                selectedRegistrationTaxTypeId === opt.registrationTaxTypeId;
              const details = taxTypeDetailsMap?.[opt.taxTypes.primary];
              const badge =
                IndirectTaxRegistrationHelper.getTaxTypeRecommendationBadge(
                  details?.recommendedFor
                );

              return (
                <RadioCardKS
                  key={opt.registrationTaxTypeId}
                  isActive={isSelected}
                  disabled={isEditMode}
                  heading={
                    <div className="flex items-center gap-sm">
                      <span className="txt-small-bold text-text-primary">
                        {IndirectTaxRegistrationHelper.getTaxTypeLabel(
                          opt,
                          taxTypeDetailsMap
                        )}
                      </span>
                      {badge && (
                        <Badge size="sm" variant={badge.variant}>
                          {badge.label}
                        </Badge>
                      )}
                    </div>
                  }
                  description={
                    details?.fullDescription ? (
                      <p className="txt-small-regular text-text-tertiary">
                        {details.fullDescription}
                      </p>
                    ) : undefined
                  }
                  onClick={() =>
                    onTaxTypeOptionChange(
                      opt.registrationTaxTypeId,
                      requiredTaxTypes
                    )
                  }
                />
              );
            })}
          </div>

          {selectedRegistrationTaxTypeId && optionalTaxTypes.length > 0 && (
            <div className="flex flex-col gap-sm p-xl rounded-lg bg-bg-secondary border border-border-primary">
              <p className="txt-small-bold text-text-secondary">
                Optional additional tax types
              </p>
              {optionalTaxTypes.map((taxType) => {
                const optDetails = taxTypeDetailsMap?.[taxType];
                return (
                  <div
                    key={taxType}
                    className="flex items-start gap-sm p-sm rounded-md bg-bg-primary border border-border-primary"
                  >
                    <Checkbox
                      id={`optional-${taxType}`}
                      checked={selectedTaxTypes.includes(taxType)}
                      onCheckedChange={(checked) =>
                        onOptionalTaxTypeToggle(taxType, checked as boolean)
                      }
                      className="mt-0.5"
                    />
                    <div className="flex flex-col gap-xs">
                      <label
                        htmlFor={`optional-${taxType}`}
                        className="txt-small-medium text-text-secondary cursor-pointer"
                      >
                        {IndirectTaxRegistrationHelper.getTaxTypeDisplayName(
                          taxType,
                          taxTypeDetailsMap
                        )}
                      </label>
                      <p className="txt-tiny-regular text-text-tertiary">
                        {optDetails?.fullDescription || ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedRegistrationTaxTypeId && !hideFrequencyAndDate && (
        <>
          <div className="flex flex-col gap-xs">
            <p className="txt-small-medium text-text-secondary">
              Filing frequency
              {!isEditMode && (
                <span className="text-utility-violet-600 ml-0.5">*</span>
              )}
            </p>
            <SingleCombobox
              value={selectedFrequency}
              setValue={onFrequencyChange}
              options={frequencyOptions}
              placeholder="Select frequency"
              _className="txt-medium-regular text-text-secondary h-11 px-3"
              canClearSelection={false}
              hideSearch
            />
          </div>

          <div className="flex flex-col gap-xs">
            <p className="txt-small-medium text-text-secondary">
              Effective registration date
              {!isEditMode && (
                <span className="text-utility-violet-600 ml-0.5">*</span>
              )}
            </p>
            <DatePicker
              date={effectiveStartDate}
              setDate={onEffectiveDateChange}
              disabled={{ after: new Date() }}
              _triggerClassName="txt-medium-regular text-text-secondary h-11 px-3"
            />
          </div>
        </>
      )}
    </div>
  );
}
